import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowRight, Loader2, Users, Bus, HelpCircle, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_lunch-roster-hub/artifacts/wio7p25o_rr%20logo.png';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const GroupLogin = () => {
  const [groupNumber, setGroupNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const { loginGroup, isGroupLoggedIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isGroupLoggedIn) {
      navigate('/order');
    }
  }, [isGroupLoggedIn, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!groupNumber.trim()) {
      toast.error('Please enter your group number');
      return;
    }
    setLoading(true);
    try {
      const result = await loginGroup(groupNumber.trim());
      if (result.success) {
        toast.success(`Welcome, ${result.group.name}!`);
        navigate('/order');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Group not found. Try B1, B02, or group name like Lobsters');
      setGroupNumber('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 relative">
      {/* Top Links */}
      <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
        <a
          href="https://campersnapshot.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 shadow-sm text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-[#2B4F8C] transition-colors"
          data-testid="campersnapshot-link"
        >
          Back to CamperSnapshot
        </a>
        {!helpOpen ? (
          <button
            onClick={() => setHelpOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 shadow-sm text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-[#2B4F8C] transition-colors"
            data-testid="help-btn"
          >
            <HelpCircle className="w-4 h-4" />
            Help
          </button>
        ) : (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setHelpOpen(false)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="bg-[#2B4F8C] text-white p-5 text-center">
                <HelpCircle className="w-8 h-8 mx-auto mb-2" />
                <h2 className="text-lg font-bold">Help Guides</h2>
                <p className="text-blue-200 text-sm">Choose your guide</p>
              </div>
              <div className="p-4 space-y-3">
                <a
                  href={`${API_URL}/api/admin/counselor-sop-pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 rounded-xl border-2 border-slate-200 hover:border-[#2B4F8C] hover:bg-blue-50 transition-all"
                  data-testid="help-counselor-btn"
                >
                  <p className="font-bold text-slate-900 text-base">Counselor Ordering</p>
                  <p className="text-sm text-slate-500 mt-1">How to order lunch for your group</p>
                </a>
                <a
                  href={`${API_URL}/api/admin/sop-pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 rounded-xl border-2 border-slate-200 hover:border-[#2B4F8C] hover:bg-blue-50 transition-all"
                  data-testid="help-admin-btn"
                >
                  <p className="font-bold text-slate-900 text-base">Supervisor / Dining Staff</p>
                  <p className="text-sm text-slate-500 mt-1">Admin panel, reports, and kitchen view</p>
                </a>
              </div>
              <div className="p-4 pt-0">
                <button
                  onClick={() => setHelpOpen(false)}
                  className="w-full py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="w-full max-w-md page-enter">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img src={LOGO_URL} alt="Rolling River Day Camp" className="w-32 h-32 object-contain" data-testid="camp-logo" />
        </div>

        <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
          <div className="bg-[#2B4F8C] text-white p-5 text-center">
            <h1 className="text-2xl font-black tracking-tight">RRDC Lunch Order</h1>
            <p className="text-blue-200 text-sm mt-1">Rolling River Day Camp</p>
          </div>
          <CardContent className="p-6 space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-600 mb-2">
                  <Users className="w-4 h-4" />
                  Group Login
                </label>
                <Input
                  type="text"
                  value={groupNumber}
                  onChange={(e) => setGroupNumber(e.target.value)}
                  placeholder="Enter group number or name..."
                  className="h-14 text-xl text-center font-semibold border-2"
                  autoFocus
                  data-testid="group-input"
                />
                <p className="text-xs text-slate-500 mt-1 text-center">Enter B1, B02, G1 or group name</p>
              </div>
              <Button
                type="submit"
                disabled={!groupNumber.trim() || loading}
                className="w-full h-14 text-lg font-bold bg-[#2B4F8C] hover:bg-[#1E3A6B] transition-all active:scale-95 disabled:opacity-50"
                data-testid="login-submit-btn"
              >
                {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : <>Continue <ArrowRight className="w-5 h-5 ml-2" /></>}
              </Button>
            </form>

            {/* Divider - Trips */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 border-t border-slate-200" />
              <span className="text-xs text-slate-400 font-medium uppercase">Trips</span>
              <div className="flex-1 border-t border-slate-200" />
            </div>

            {/* Trips Button */}
            <Button
              variant="outline"
              className="w-full h-12 text-base font-bold border-2 border-[#FDB813] text-[#2B4F8C] hover:bg-[#FDB813]/10"
              onClick={() => navigate('/trips')}
              data-testid="trips-btn"
            >
              <Bus className="w-5 h-5 mr-2" />
              Trip Lunch Orders
            </Button>
          </CardContent>
        </Card>

        {/* Admin Link */}
        <div className="mt-8 text-center">
          <button onClick={() => navigate('/admin')} className="text-slate-500 text-sm hover:text-[#2B4F8C] transition-colors" data-testid="admin-link">
            Kitchen / Admin Access
          </button>
        </div>

        <div className="mt-4 text-center text-xs text-slate-400">All times in Eastern Standard Time (EST)</div>
      </div>
    </div>
  );
};

export default GroupLogin;
